// SCR-STAFF-603 — قالب‌های رضایت‌نامه (US-DOC-040): فهرست با نوع، نسخه و تاریخ بازبینی حقوقی بعدی، کتابخانه رضایت‌نامه‌های نمونه بسته‌ها (نشان
// «نمونه» — IsSample)، ویرایشگر (عنوان، متن با متغیرها، خطرات، بندهای تأییدی، فیلدهای اضافه، امضاکنندگان، مدت اعتبار) و انتشار نسخه با گزینه
// «نیازمند امضای مجدد» (رضایت‌های قبلی ← RenewalRequired — AC-DOC-040.3).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type DocConsentTemplateView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, JalaliDate, ListPage, SampleBadge, SampleContentBanner, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { docKeys, num, SCR_STAFF_603 } from './common';

const KINDS = ['InformedConsent', 'Waiver', 'PhotoConsent', 'Other'] as const;
export const CONSENT_VARIABLES = ['patient_name', 'service_name', 'provider_name', 'date', 'branch_name', 'tenant_name'];

interface ConsentContentJson {
  title: string;
  body: string;
  risks?: string | null;
  clauses: { key: string; text: string; required: boolean }[];
  extraFields: { key: string; label: string; required: boolean }[];
  signers: { patient: boolean; guardianForMinor: boolean; minorSelf: boolean; witness: boolean; provider: boolean };
  validity: { kind: 'Months' | 'Session' | 'Treatment'; months?: number | null };
}

function emptyContent(): ConsentContentJson {
  return {
    title: '',
    body: '',
    risks: '',
    clauses: [],
    extraFields: [],
    signers: { patient: true, guardianForMinor: true, minorSelf: false, witness: false, provider: false },
    validity: { kind: 'Months', months: 12 },
  };
}

function linesOf<T>(items: T[], format: (item: T) => string): string {
  return items.map(format).join('\n');
}

function EditorDialog({ template, onClose }: { template: DocConsentTemplateView | null; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const initial = (template?.content as ConsentContentJson | null) ?? emptyContent();
  const [code, setCode] = useState(template?.code ?? '');
  const [name, setName] = useState(template?.name ?? '');
  const [kind, setKind] = useState<string>(template?.kind ?? 'InformedConsent');
  const [content, setContent] = useState<ConsentContentJson>({ ...emptyContent(), ...initial });
  const [clauses, setClauses] = useState(linesOf(initial.clauses ?? [], (clause) => `${clause.key}|${clause.text}${clause.required ? '' : '|optional'}`));
  const [extras, setExtras] = useState(linesOf(initial.extraFields ?? [], (field) => `${field.key}|${field.label}${field.required ? '' : '|optional'}`));
  const [dirty, setDirty] = useState(false);

  const build = (): ConsentContentJson => ({
    ...content,
    risks: content.risks || null,
    clauses: clauses
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [key, text, flag] = line.split('|');
        return { key: (key ?? '').trim(), text: (text ?? '').trim(), required: flag?.trim() !== 'optional' };
      }),
    extraFields: extras
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [key, label, flag] = line.split('|');
        return { key: (key ?? '').trim(), label: (label ?? '').trim(), required: flag?.trim() !== 'optional' };
      }),
  });

  const save = useMutation({
    mutationFn: () =>
      template
        ? unwrap(tenantApi.PUT('/api/v1/documents/consent-templates/{id}', { params: { path: { id: template.id } }, body: { code: null, name, kind: kind as 'Other', content: build() as never, rowVersion: num(template.rowVersion) } }))
        : unwrap(tenantApi.POST('/api/v1/documents/consent-templates', { body: { code, name, kind: kind as 'Other', content: build() as never, rowVersion: null } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.consentTemplates(session?.tenantId) });
      onClose();
    },
  });
  const set = (patch: Partial<ConsentContentJson>) => {
    setDirty(true);
    setContent((current) => ({ ...current, ...patch }));
  };

  return (
    <FormDialog open titleKey={template ? 'doc:consent_templates.edit_title' : 'doc:consent_templates.create_title'} screenId={SCR_STAFF_603} onSubmit={() => save.mutate()} onClose={onClose} isDirty={dirty} isSubmitting={save.isPending} submitError={save.error} maxWidth="md">
      {template?.isSample ? (
        <SampleContentBanner>
          <SampleBadge /> {t('doc:consent_templates.sample_hint')}
        </SampleContentBanner>
      ) : null}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <TextField label={t('doc:builder.code')} value={code} onChange={(event) => setCode(event.target.value)} disabled={!!template} required slotProps={{ htmlInput: { 'data-testid': 'consent-template-code', dir: 'ltr' } }} />
        <TextField label={t('doc:builder.name')} value={name} onChange={(event) => { setDirty(true); setName(event.target.value); }} fullWidth required slotProps={{ htmlInput: { 'data-testid': 'consent-template-name' } }} />
        <TextField select label={t('doc:consent_templates.kind')} value={kind} onChange={(event) => { setDirty(true); setKind(event.target.value); }} sx={{ minWidth: 180 }}>
          {KINDS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`doc:consent_kind.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <TextField label={t('doc:consent_templates.title_field')} value={content.title} onChange={(event) => set({ title: event.target.value })} required slotProps={{ htmlInput: { 'data-testid': 'consent-template-title' } }} />
      <TextField label={t('doc:consent_templates.body')} value={content.body} onChange={(event) => set({ body: event.target.value })} multiline minRows={5} required slotProps={{ htmlInput: { 'data-testid': 'consent-template-body' } }} />
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }} aria-label={t('doc:consent_templates.variables')}>
        {CONSENT_VARIABLES.map((variable) => (
          <Chip key={variable} size="small" label={`{${variable}}`} onClick={() => set({ body: `${content.body}{${variable}}` })} />
        ))}
      </Stack>
      <TextField label={t('doc:consent_templates.risks')} value={content.risks ?? ''} onChange={(event) => set({ risks: event.target.value })} multiline minRows={2} />
      <TextField label={t('doc:consent_templates.clauses')} helperText={t('doc:consent_templates.clauses_hint')} value={clauses} onChange={(event) => { setDirty(true); setClauses(event.target.value); }} multiline minRows={3} slotProps={{ htmlInput: { 'data-testid': 'consent-template-clauses' } }} />
      <TextField label={t('doc:consent_templates.extra_fields')} helperText={t('doc:consent_templates.extra_hint')} value={extras} onChange={(event) => { setDirty(true); setExtras(event.target.value); }} multiline minRows={2} />
      <Typography variant="subtitle2">{t('doc:consent_templates.signers')}</Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
        {(['patient', 'guardianForMinor', 'minorSelf', 'witness', 'provider'] as const).map((key) => (
          <FormControlLabel
            key={key}
            control={<Checkbox checked={content.signers[key]} onChange={(event) => set({ signers: { ...content.signers, [key]: event.target.checked } })} slotProps={{ input: { 'data-testid': `consent-template-signer-${key}` } as never }} />}
            label={t(`doc:consent_templates.signer.${key}`)}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField select label={t('doc:consent_templates.validity')} value={content.validity.kind} onChange={(event) => set({ validity: { kind: event.target.value as 'Months', months: event.target.value === 'Months' ? (content.validity.months ?? 12) : null } })} sx={{ minWidth: 180 }}>
          {(['Months', 'Session', 'Treatment'] as const).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`doc:validity.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        {content.validity.kind === 'Months' ? (
          <TextField type="number" label={t('doc:consent_templates.months')} value={content.validity.months ?? 12} onChange={(event) => set({ validity: { kind: 'Months', months: Number(event.target.value) } })} sx={{ width: 140 }} />
        ) : null}
      </Stack>
    </FormDialog>
  );
}

function PublishDialog({ template, onClose }: { template: DocConsentTemplateView; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [resign, setResign] = useState(false);
  const [months, setMonths] = useState('12');
  const publish = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/consent-templates/{id}:publish', { params: { path: { id: template.id } }, body: { requiresResign: resign, legalReviewMonths: Number(months) || null, rowVersion: num(template.rowVersion) } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="doc:consent_templates.publish_title" screenId={SCR_STAFF_603} onSubmit={() => publish.mutate()} onClose={onClose} isDirty={false} isSubmitting={publish.isPending} submitError={publish.error} submitKey="doc:builder.publish">
      {num(template.currentVersion) > 0 ? (
        <FormControlLabel control={<Checkbox checked={resign} onChange={(event) => setResign(event.target.checked)} slotProps={{ input: { 'data-testid': 'consent-publish-resign' } as never }} />} label={t('doc:consent_templates.requires_resign')} />
      ) : null}
      {resign ? <Alert severity="warning">{t('doc:consent_templates.resign_hint')}</Alert> : null}
      <TextField type="number" label={t('doc:consent_templates.legal_review_months')} value={months} onChange={(event) => setMonths(event.target.value)} />
    </FormDialog>
  );
}

function LibraryDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const library = useQuery({ queryKey: docKeys.consentLibrary(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/consent-templates/library')) });
  const importSample = useMutation({
    mutationFn: (sampleRef: string) => unwrap(tenantApi.POST('/api/v1/documents/consent-templates:import-sample', { body: { sampleRef } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="doc:consent_templates.library_title" screenId={SCR_STAFF_603} onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={importSample.isPending} submitError={importSample.error} submitKey="doc:actions.close">
      <Alert severity="info">{t('doc:consent_templates.library_hint')}</Alert>
      {library.error ? <ErrorState error={library.error} /> : null}
      {(library.data?.items ?? []).map((item) => (
        <Stack key={item.sampleRef} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <SampleBadge />
          <Typography sx={{ flexGrow: 1 }}>{item.name}</Typography>
          <Button size="small" disabled={item.imported || importSample.isPending} onClick={() => importSample.mutate(item.sampleRef)} data-testid={`consent-library-import-${item.sampleRef}`}>
            {item.imported ? t('doc:builder.imported') : t('doc:builder.import')}
          </Button>
        </Stack>
      ))}
    </FormDialog>
  );
}

export function ConsentTemplatesPage() {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('doc.consent_template.manage');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<DocConsentTemplateView | null | 'new'>(null);
  const [publishing, setPublishing] = useState<DocConsentTemplateView | null>(null);
  const [library, setLibrary] = useState(false);
  const templates = useQuery({ queryKey: docKeys.consentTemplates(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/consent-templates')) });
  const archive = useMutation({
    mutationFn: (row: DocConsentTemplateView) => unwrap(tenantApi.POST('/api/v1/documents/consent-templates/{id}:archive', { params: { path: { id: row.id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) }),
  });
  const open = async (row: DocConsentTemplateView) => setEditing(await unwrap(tenantApi.GET('/api/v1/documents/consent-templates/{id}', { params: { path: { id: row.id } } })));

  const columns: DataTableColumn<DocConsentTemplateView>[] = [
    {
      id: 'name',
      headerKey: 'doc:builder.columns.name',
      cell: (row) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{row.name}</span>
          {row.isSample ? <SampleBadge /> : null}
        </Stack>
      ),
    },
    { id: 'kind', headerKey: 'doc:consent_templates.kind', cell: (row) => t(`doc:consent_kind.${row.kind}`, { defaultValue: row.kind }) },
    { id: 'status', headerKey: 'doc:builder.columns.status', cell: (row) => <span data-testid={`consent-template-status-${row.code}`}>{t(`doc:template_status.${row.status}`)}</span> },
    { id: 'version', headerKey: 'doc:builder.columns.version', cell: (row) => (num(row.currentVersion) > 0 ? faDigits(num(row.currentVersion)) : '—') + (row.hasDraftChanges ? ` · ${t('doc:builder.draft_changes')}` : '') },
    { id: 'review', headerKey: 'doc:consent_templates.next_review', cell: (row) => (row.nextLegalReviewOn ? <JalaliDate value={row.nextLegalReviewOn} /> : '—') },
    {
      id: 'actions',
      headerKey: 'doc:builder.columns.actions',
      cell: (row) =>
        write.canWrite ? (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={() => void open(row)} data-testid={`consent-template-edit-${row.code}`}>
              {t('doc:actions.edit')}
            </Button>
            {row.hasDraftChanges && row.status !== 'Archived' ? (
              <Button size="small" onClick={() => setPublishing(row)} data-testid={`consent-template-publish-${row.code}`}>
                {t('doc:builder.publish')}
              </Button>
            ) : null}
            {row.status !== 'Archived' ? (
              <Button size="small" color="warning" onClick={() => archive.mutate(row)}>
                {t('doc:builder.archive')}
              </Button>
            ) : null}
          </Stack>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_603}
      titleKey="doc:consent_templates.title"
      subtitleKey="doc:consent_templates.subtitle"
      actions={
        write.canWrite ? (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setLibrary(true)} data-testid="consent-library">
              {t('doc:builder.library')}
            </Button>
            <Button variant="contained" onClick={() => setEditing('new')} data-testid="consent-template-create">
              {t('doc:consent_templates.create')}
            </Button>
          </Stack>
        ) : undefined
      }
      filters={<WriteNotice write={write} />}
    >
      {archive.error ? <ErrorState error={archive.error} compact /> : null}
      <DataTable captionKey="doc:consent_templates.title" columns={columns} rows={templates.data?.items} getRowId={(row) => row.id} isLoading={templates.isPending} error={templates.error} onRetry={() => void templates.refetch()} emptyTitleKey="doc:consent_templates.empty" />
      {editing ? <EditorDialog template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {publishing ? <PublishDialog template={publishing} onClose={() => setPublishing(null)} /> : null}
      {library ? <LibraryDialog onClose={() => setLibrary(false)} /> : null}
    </ListPage>
  );
}
